import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  authApi,
  employeeAttendanceApi,
  employeeLeaveCreditsApi,
  employeeLeaveRequestsApi,
  employeeProjectsApi,
  employeeTaskTrackerApi,
  type EmployeeTimeEntryDto,
  type EmployeeLeaveCreditDto,
  type EmployeeLeaveRequestDto,
  type EmployeeTaskColumnDto,
} from '../services/api';

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

type TaskColumn = {
  id: string;
  title: string;
  tasks: { id: string; title: string; projectId?: string | null; projectName?: string | null }[];
};

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<EmployeeTimeEntryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<'in' | 'out' | null>(null);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakStartAt, setBreakStartAt] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'attendance' | 'leave' | 'tasks'>('attendance');
  const [leaveCredits, setLeaveCredits] = useState<EmployeeLeaveCreditDto[]>([]);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  const [leaveRequests, setLeaveRequests] = useState<EmployeeLeaveRequestDto[]>([]);
  const [leaveRequestsLoading, setLeaveRequestsLoading] = useState(false);
  const [isApplyLeaveOpen, setIsApplyLeaveOpen] = useState(false);
  const [selectedLeaveCreditId, setSelectedLeaveCreditId] = useState<string>('');
  const [leaveDateFrom, setLeaveDateFrom] = useState('');
  const [leaveDateTo, setLeaveDateTo] = useState('');
  const [leaveReason, setLeaveReason] = useState('');
  const [leaveSubmitError, setLeaveSubmitError] = useState('');
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);
  const [taskColumns, setTaskColumns] = useState<TaskColumn[]>([]);
  const [taskLoading, setTaskLoading] = useState(false);
  const [taskError, setTaskError] = useState('');
  const [employeeProjects, setEmployeeProjects] = useState<{ id: string; project_name: string }[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [completedTasks, setCompletedTasks] = useState<{ id: string; title: string; project_name: string | null; sprint_name: string | null; completed_at: string | null }[]>([]);
  const [completedTasksLoading, setCompletedTasksLoading] = useState(false);

  const mapColumnsFromApi = (cols: EmployeeTaskColumnDto[]): TaskColumn[] =>
    cols.map((c) => ({
      id: c.id,
      title: c.title,
      tasks: (c.tasks ?? []).map((t) => ({
        id: t.id,
        title: t.title,
        projectId: t.project_id ?? null,
        projectName: t.project_name ?? null,
      })),
    }));

  const fetchTaskTracker = useCallback(async () => {
    setTaskError('');
    setTaskLoading(true);
    try {
      const res = await employeeTaskTrackerApi.list();
      let data = res.data ?? [];
      if (data.length === 0) {
        await employeeTaskTrackerApi.createColumn({ title: 'Planned', sort_order: 0 });
        await employeeTaskTrackerApi.createColumn({ title: 'In progress', sort_order: 1 });
        await employeeTaskTrackerApi.createColumn({ title: 'Done', sort_order: 2 });
        const refetch = await employeeTaskTrackerApi.list();
        data = refetch.data ?? [];
      }
      setTaskColumns(mapColumnsFromApi(data));
      const completedRes = await employeeTaskTrackerApi.listCompleted();
      if (completedRes.data) setCompletedTasks(completedRes.data);
    } catch {
      setTaskError('Could not load task tracker.');
      setTaskColumns([]);
    } finally {
      setTaskLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'tasks') return;
    let cancelled = false;
    setProjectsLoading(true);
    employeeProjectsApi
      .list()
      .then((res) => {
        if (!cancelled && res.data) setEmployeeProjects(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'tasks') return;
    let cancelled = false;
    setCompletedTasksLoading(true);
    employeeTaskTrackerApi
      .listCompleted()
      .then((res) => {
        if (!cancelled && res.data) setCompletedTasks(res.data);
        else if (!cancelled) setCompletedTasks([]);
      })
      .catch(() => {
        if (!cancelled) setCompletedTasks([]);
      })
      .finally(() => {
        if (!cancelled) setCompletedTasksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const filteredTaskColumns = useMemo(() => {
    if (!selectedProjectId) return taskColumns;
    return taskColumns.map((col) => ({
      ...col,
      tasks: col.tasks.filter((t) => t.projectId === selectedProjectId),
    }));
  }, [taskColumns, selectedProjectId]);

  const handleMoveColumn = async (columnId: string, direction: 'left' | 'right') => {
    const index = taskColumns.findIndex((c) => c.id === columnId);
    if (index === -1) return;
    const targetIndex = direction === 'left' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= taskColumns.length) return;
    const next = [...taskColumns];
    const [removed] = next.splice(index, 1);
    next.splice(targetIndex, 0, removed);
    const columnIds = next.map((c) => c.id);
    try {
      await employeeTaskTrackerApi.reorderColumns(columnIds);
      setTaskColumns(next);
    } catch {
      setTaskError('Could not reorder columns.');
    }
  };

  const handleDeleteColumn = async (columnId: string) => {
    if (taskColumns.length <= 1) return;
    const column = taskColumns.find((c) => c.id === columnId);
    if (column?.tasks.length) {
      if (!window.confirm('This column has tasks. Deleting it will also remove its tasks. Continue?')) return;
    }
    try {
      await employeeTaskTrackerApi.deleteColumn(columnId);
      setTaskColumns((prev) => prev.filter((c) => c.id !== columnId));
    } catch {
      setTaskError('Could not delete column.');
    }
  };

  const handleAddColumn = async () => {
    const name = window.prompt('Column name');
    if (!name || !name.trim()) return;
    try {
      const res = await employeeTaskTrackerApi.createColumn({ title: name.trim() });
      if (res.data) {
        setTaskColumns((prev) => [...prev, mapColumnsFromApi([res.data!])[0]]);
      }
    } catch {
      setTaskError('Could not add column.');
    }
  };

  const handleAddTask = async (columnId: string) => {
    const title = window.prompt('What are you working on?');
    if (!title || !title.trim()) return;
    try {
      const res = await employeeTaskTrackerApi.createTask({ column_id: columnId, title: title.trim() });
      if (res.data) {
        setTaskColumns((prev) =>
          prev.map((col) =>
            col.id === columnId
              ? { ...col, tasks: [...col.tasks, { id: res.data!.id, title: res.data!.title }] }
              : col,
          ),
        );
      }
    } catch {
      setTaskError('Could not add task.');
    }
  };

  const handleMoveTask = async (
    fromColumnId: string,
    taskId: string,
    direction: 'left' | 'right',
  ) => {
    const fromIndex = taskColumns.findIndex((c) => c.id === fromColumnId);
    if (fromIndex === -1) return;
    const toIndex = direction === 'left' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= taskColumns.length) return;
    const fromColumn = taskColumns[fromIndex];
    const toColumn = taskColumns[toIndex];
    const task = fromColumn.tasks.find((t) => t.id === taskId);
    if (!task) return;
    const toColumnId = toColumn.id;
    try {
      await employeeTaskTrackerApi.updateTask(taskId, { column_id: toColumnId });
      setTaskColumns((prev) => {
        const next = [...prev];
        next[fromIndex] = {
          ...fromColumn,
          tasks: fromColumn.tasks.filter((t) => t.id !== taskId),
        };
        next[toIndex] = {
          ...toColumn,
          tasks: [...toColumn.tasks, task],
        };
        return next;
      });
    } catch {
      setTaskError('Could not move task.');
    }
  };

  const fetchToday = useCallback(async () => {
    setError('');
    try {
      const res = await employeeAttendanceApi.today();
      if ((res as { message?: string }).message?.includes('Not an employee')) {
        navigate('/dashboard');
        return;
      }
      if (res.data) setEntries(res.data);
      else setEntries([]);
    } catch {
      setError('Could not load today\'s attendance.');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchLeaveCredits = useCallback(async () => {
    setLeaveError('');
    setLeaveLoading(true);
    try {
      const res = await employeeLeaveCreditsApi.list();
      if (res.data) setLeaveCredits(res.data);
      else setLeaveCredits([]);
    } catch {
      setLeaveError('Could not load leave credits.');
      setLeaveCredits([]);
    } finally {
      setLeaveLoading(false);
    }
  }, []);

  const fetchLeaveRequests = useCallback(async () => {
    setLeaveRequestsLoading(true);
    try {
      const res = await employeeLeaveRequestsApi.list();
      if (res.data) setLeaveRequests(res.data);
      else setLeaveRequests([]);
    } catch {
      // silently ignore for now; could add error UI later
      setLeaveRequests([]);
    } finally {
      setLeaveRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const raw = localStorage.getItem('user_data');
      let email: string | null = null;

      if (raw) {
        try {
          const u = JSON.parse(raw) as {
            id?: string;
            employee_id?: string;
            email?: string;
            name?: string;
            user_type?: string;
          };
          setUserName(u.name || u.email || 'Employee');
          email = u.email ?? null;
          if (email) setUserEmail(email);

          if (u.user_type && u.user_type !== 'employee') {
            navigate('/dashboard');
            return;
          }
        } catch {
          setUserName('Employee');
        }
      }

      // Restore break state from localStorage if present
      const storedBreakStart = localStorage.getItem('employee_break_start_at');
      if (storedBreakStart) {
        const ts = Number(storedBreakStart);
        if (!Number.isNaN(ts)) {
          setBreakStartAt(ts);
          setIsOnBreak(true);
        } else {
          localStorage.removeItem('employee_break_start_at');
        }
      }

      // For employee users, load their own leave credits, leave requests, and task tracker via the self-service API.
      void fetchLeaveCredits();
      void fetchLeaveRequests();
      void fetchTaskTracker();

      await fetchToday();
    };

    void init();
  }, [fetchLeaveCredits, fetchLeaveRequests, fetchTaskTracker, fetchToday, navigate]);

  const handleClockIn = async () => {
    setError('');
    setActionLoading('in');
    try {
      const res = await employeeAttendanceApi.clockIn();
      if (res.data) {
        setEntries((prev) => [...prev, res.data!]);
      }
      if (res.message && res.message.includes('already clocked in')) {
        await fetchToday();
      }
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message || 'Clock in failed.';
      setError(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleClockOut = async () => {
    setError('');
    // If user is on break, end the break without adding extra minutes
    if (isOnBreak) {
      setIsOnBreak(false);
      setBreakStartAt(null);
      localStorage.removeItem('employee_break_start_at');
    }
    setActionLoading('out');
    try {
      const res = await employeeAttendanceApi.clockOut();
      if (res.data) {
        setEntries((prev) =>
          prev.map((e) =>
            e.id === res.data!.id ? { ...e, clocked_out_at: res.data!.clocked_out_at } : e
          )
        );
      }
      if (res.message && res.message.includes('No active clock-in')) {
        setError(res.message);
      } else {
        await fetchToday();
      }
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message || 'Clock out failed.';
      setError(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBreakStart = () => {
    setError('');
    const now = Date.now();
    setBreakStartAt(now);
    setIsOnBreak(true);
    localStorage.setItem('employee_break_start_at', String(now));
  };

  const handleBreakStop = async () => {
    if (!breakStartAt) return;
    const now = Date.now();
    const diffMinutes = Math.max(1, Math.round((now - breakStartAt) / (1000 * 60)));
    setError('');
    try {
      const res = await employeeAttendanceApi.addBreakMinutes(diffMinutes);
      if (res.data) {
        setEntries((prev) =>
          prev.map((e) =>
            e.id === res.data!.id ? { ...e, break_minutes: res.data!.break_minutes } : e,
          ),
        );
      }
      await fetchToday();
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message || 'Failed to record break time.';
      setError(msg);
    } finally {
      setIsOnBreak(false);
      setBreakStartAt(null);
      localStorage.removeItem('employee_break_start_at');
    }
  };

  const handleLogout = async () => {
    await authApi.logout();
    navigate('/employee-login');
  };

  const isClockedIn =
    entries.length > 0 && entries[entries.length - 1].clocked_out_at == null;

  const firstEntry = entries[0];
  const lastEntry = entries[entries.length - 1];

  const completedEntries = entries.filter((e) => e.clocked_out_at);
  const totalMs = completedEntries.reduce((sum, e) => {
    const start = new Date(e.clocked_in_at).getTime();
    const end = new Date(e.clocked_out_at!).getTime();
    const diffMs = Math.max(end - start, 0);
    const breakMs = (e.break_minutes ?? 0) * 60 * 1000;
    return sum + Math.max(diffMs - breakMs, 0);
  }, 0);

  const runningMs =
    isClockedIn && lastEntry
      ? Math.max(Date.now() - new Date(lastEntry.clocked_in_at).getTime(), 0)
      : 0;

  const totalHoursToday = (totalMs + runningMs) / (1000 * 60 * 60);

  const formatHours = (hrs: number) =>
    hrs <= 0 ? '0h' : `${hrs.toFixed(1).replace(/\.0$/, '')}h`;

  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const handleOpenApplyLeave = () => {
    setLeaveSubmitError('');
    setLeaveDateFrom('');
    setLeaveDateTo('');
    setLeaveReason('');
    if (!selectedLeaveCreditId && leaveCredits.length > 0) {
      setSelectedLeaveCreditId(leaveCredits[0].id);
    }
    setIsApplyLeaveOpen(true);
  };

  const handleCloseApplyLeave = () => {
    if (leaveSubmitting) return;
    setIsApplyLeaveOpen(false);
  };

  const handleSubmitLeave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLeaveSubmitError('');

    if (!selectedLeaveCreditId || !leaveDateFrom || !leaveDateTo || !leaveReason.trim()) {
      setLeaveSubmitError('Please complete all fields before submitting your leave request.');
      return;
    }

    const from = new Date(leaveDateFrom);
    const to = new Date(leaveDateTo);
    if (to < from) {
      setLeaveSubmitError('End date cannot be earlier than start date.');
      return;
    }

    setLeaveSubmitting(true);
    try {
      const res = await employeeLeaveRequestsApi.create({
        leave_credit_id: selectedLeaveCreditId,
        date_from: leaveDateFrom,
        date_to: leaveDateTo,
        reason: leaveReason.trim(),
      });

      if ((res as { errors?: Record<string, string[]> }).errors) {
        const firstError = Object.values(
          (res as { errors: Record<string, string[]> }).errors,
        )[0]?.[0];
        setLeaveSubmitError(firstError || 'Unable to submit leave request. Please try again.');
        return;
      }

      setIsApplyLeaveOpen(false);
      setLeaveDateFrom('');
      setLeaveDateTo('');
      setLeaveReason('');
      void fetchLeaveRequests();
    } catch {
      setLeaveSubmitError('Unable to submit leave request. Please try again.');
    } finally {
      setLeaveSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-gray-50">
      <header className="border-b border-white/10 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-500 text-lg font-bold text-white shadow-lg shadow-brand-500/40">
              S
            </span>
            <span className="text-lg font-semibold tracking-tight text-white">
              SpynERP
            </span>
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="rounded-full border border-white/10 px-3 py-1.5 text-xs font-medium text-gray-200 transition hover:border-white/20 hover:bg-white/5 hover:text-white"
          >
            Log out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <section className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-brand-300">
                Employee portal
              </p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-white md:text-4xl">
                Welcome back, {userName}
              </h1>
              <p className="mt-1 text-sm text-slate-300">{today}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${
                  isClockedIn
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                    : 'border-slate-400/30 bg-slate-500/10 text-slate-200'
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    isClockedIn ? 'bg-emerald-400' : 'bg-slate-300'
                  }`}
                />
                {isClockedIn ? (isOnBreak ? 'On break' : 'Clocked in') : 'Not clocked in'}
              </span>
              {firstEntry && (
                <span className="text-xs text-slate-300">
                  First in:{' '}
                  <span className="font-medium">
                    {formatTime(firstEntry.clocked_in_at)}
                  </span>
                </span>
              )}
            </div>
          </section>
          <section className="mb-4 flex justify-start overflow-x-auto">
            <div className="inline-flex rounded-full border border-white/10 bg-slate-900/80 p-1 text-xs shrink-0">
              <button
                type="button"
                onClick={() => setActiveTab('attendance')}
                className={`px-3 py-1.5 rounded-full font-medium transition-all ${
                  activeTab === 'attendance'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                Attendance
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('leave')}
                className={`px-3 py-1.5 rounded-full font-medium transition-all ${
                  activeTab === 'leave'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                Leave credits
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('tasks')}
                className={`px-3 py-1.5 rounded-full font-medium transition-all ${
                  activeTab === 'tasks'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                Task tracker
              </button>
            </div>
          </section>

          {activeTab === 'attendance' ? (
            <section className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)]">
              <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 to-slate-950/70 p-6 shadow-xl shadow-black/40">
                <div className="mb-4 flex items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold tracking-tight text-white">
                    Time in / Time out
                  </h2>
                  <p className="text-[11px] text-slate-400">
                    Track your attendance for today
                  </p>
                </div>

                {error && (
                  <div className="mb-4 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs text-red-100">
                    {error}
                  </div>
                )}

                {loading ? (
                  <p className="text-sm text-slate-300">Loading...</p>
                ) : (
                  <>
                    <div className="mb-5 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={handleClockIn}
                        disabled={isClockedIn || actionLoading !== null}
                        className={`inline-flex flex-1 min-w-[120px] items-center justify-center rounded-2xl px-5 py-3 text-sm font-semibold shadow-md transition-all md:flex-none md:px-6 ${
                          isClockedIn || actionLoading
                            ? 'cursor-not-allowed bg-slate-700/60 text-slate-400 shadow-none'
                            : 'bg-emerald-500 text-emerald-950 hover:bg-emerald-400 shadow-emerald-500/30'
                        }`}
                      >
                        {actionLoading === 'in' ? 'Clocking in…' : 'Time in'}
                      </button>
                      <button
                        type="button"
                        onClick={handleClockOut}
                        disabled={!isClockedIn || actionLoading !== null}
                        className={`inline-flex flex-1 min-w-[120px] items-center justify-center rounded-2xl px-5 py-3 text-sm font-semibold shadow-md transition-all md:flex-none md:px-6 ${
                          !isClockedIn || actionLoading
                            ? 'cursor-not-allowed bg-slate-800/70 text-slate-500 shadow-none'
                            : 'bg-amber-500 text-amber-950 hover:bg-amber-400 shadow-amber-500/30'
                        }`}
                      >
                        {actionLoading === 'out' ? 'Clocking out…' : 'Time out'}
                      </button>
                      <button
                        type="button"
                        onClick={isOnBreak ? handleBreakStop : handleBreakStart}
                        disabled={!isClockedIn || actionLoading !== null}
                        className={`inline-flex flex-1 min-w-[120px] items-center justify-center rounded-2xl px-5 py-3 text-sm font-semibold shadow-md transition-all md:flex-none md:px-6 ${
                          !isClockedIn || actionLoading
                            ? 'cursor-not-allowed bg-slate-800/70 text-slate-500 shadow-none'
                            : isOnBreak
                              ? 'bg-sky-500 text-sky-950 hover:bg-sky-400 shadow-sky-500/30'
                              : 'bg-slate-100 text-slate-900 hover:bg-white shadow-slate-400/30'
                        }`}
                      >
                        {isOnBreak ? 'Break stop' : 'Break start'}
                      </button>
                    </div>

                    <p className="mb-4 text-xs text-slate-300">
                      {isClockedIn
                        ? isOnBreak
                          ? 'You are currently on break. Click Break stop when you return.'
                          : "You're currently clocked in. Remember to clock out when you finish your shift."
                        : 'Click Time in when you start work to begin tracking your hours.'}
                    </p>

                    <div className="mb-4 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                    {entries.length === 0 ? (
                      <p className="text-sm text-slate-300">
                        No entries recorded for today yet.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">
                          Today&apos;s punches
                        </p>
                        <ul className="space-y-2 text-sm">
                          {entries.map((e) => {
                            const isCurrentEntry = lastEntry && e.id === lastEntry.id;
                            const recordedBreak = e.break_minutes ?? 0;
                            return (
                              <li
                                key={e.id}
                                className="flex items-center justify-between rounded-2xl border border-white/5 bg-white/5 px-3 py-2"
                              >
                                <div className="flex flex-col text-slate-100">
                                  <span className="text-[11px] uppercase tracking-[0.16em] text-slate-400">
                                    In
                                  </span>
                                  <span className="font-medium">
                                    {formatTime(e.clocked_in_at)}
                                  </span>
                                </div>
                                <div className="flex flex-col text-right text-slate-100">
                                  <span className="text-[11px] uppercase tracking-[0.16em] text-slate-400">
                                    Out
                                  </span>
                                  <span className="font-medium">
                                    {e.clocked_out_at
                                      ? formatTime(e.clocked_out_at)
                                      : '—'}
                                  </span>
                                  <span className="mt-1 text-[11px] text-slate-300">
                                    Break:{' '}
                                    <span className="font-medium">
                                      {isCurrentEntry && isOnBreak
                                        ? `${recordedBreak} min (running)`
                                        : `${recordedBreak} min`}
                                    </span>
                                  </span>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>

              <aside className="space-y-4">
                <div className="rounded-3xl border border-white/10 bg-slate-900/80 p-5 shadow-lg shadow-black/40">
                  <h2 className="mb-3 text-sm font-semibold tracking-tight text-white">
                    Today at a glance
                  </h2>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="rounded-2xl border border-white/5 bg-white/5 p-3">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                        Total hours
                      </p>
                      <p className="mt-1 text-lg font-semibold text-white">
                        {formatHours(totalHoursToday)}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-white/5 bg-white/5 p-3">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                        Last activity
                      </p>
                      <p className="mt-1 text-sm font-medium text-slate-100">
                        {lastEntry
                          ? lastEntry.clocked_out_at
                            ? `Clocked out at ${formatTime(lastEntry.clocked_out_at)}`
                            : `Clocked in at ${formatTime(lastEntry.clocked_in_at)}`
                          : '—'}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-white/5 bg-white/5 p-3">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                        Entries today
                      </p>
                      <p className="mt-1 text-lg font-semibold text-white">
                        {entries.length || 0}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-white/5 bg-white/5 p-3">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                        Status
                      </p>
                      <p className="mt-1 text-sm font-medium text-slate-100">
                        {isClockedIn ? 'On shift' : 'Off shift'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-dashed border-white/15 bg-slate-900/60 p-4 text-xs text-slate-300">
                  <p className="font-medium text-slate-100">
                    Attendance policy reminder
                  </p>
                  <p className="mt-1">
                    Make sure to clock in at the start of your workday and clock
                    out before you leave so your hours are recorded accurately.
                  </p>
                </div>
              </aside>
            </section>
          ) : activeTab === 'leave' ? (
            <section className="grid gap-6">
              <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 to-slate-950/70 p-6 shadow-xl shadow-black/40">
                <div className="mb-4 flex items-center justify-between gap-2">
                  <div>
                    <h2 className="text-sm font-semibold tracking-tight text-white">
                      Leave credits
                    </h2>
                    <p className="text-[11px] text-slate-400">
                      View your current leave balances
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenApplyLeave}
                    disabled={leaveCredits.length === 0}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-medium shadow-sm transition ${
                      leaveCredits.length === 0
                        ? 'cursor-not-allowed border-slate-600 bg-slate-800/80 text-slate-400 shadow-none'
                        : 'border-brand-400/60 bg-brand-500/90 text-slate-950 shadow-brand-500/40 hover:bg-brand-400 hover:border-brand-300'
                    }`}
                  >
                    <span className="text-base leading-none">＋</span>
                    Apply leave
                  </button>
                </div>

                {leaveError && (
                  <div className="mb-4 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs text-red-100">
                    {leaveError}
                  </div>
                )}

                {leaveLoading ? (
                  <p className="text-sm text-slate-300">Loading leave credits...</p>
                ) : leaveCredits.length === 0 ? (
                  <p className="text-sm text-slate-300">
                    No leave credits have been assigned to your record yet.
                  </p>
                ) : (
                  <>
                    <div className="space-y-3">
                      <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">
                        Available leave types
                      </p>
                      <ul className="space-y-2 text-sm">
                        {leaveCredits.map((credit) => {
                          const used = Math.max(
                            0,
                            (credit.days ?? 0) - (credit.remaining_days ?? 0),
                          );
                          return (
                            <li
                              key={credit.id}
                              className="flex items-center justify-between rounded-2xl border border-white/5 bg-white/5 px-3 py-2"
                            >
                              <div className="flex flex-col text-slate-100">
                                <span className="text-[11px] uppercase tracking-[0.16em] text-slate-400">
                                  {credit.credit_name}
                                </span>
                                <span className="text-xs text-slate-300">
                                  Employee type:{' '}
                                  <span className="capitalize">{credit.type}</span>
                                </span>
                              </div>
                              <div className="flex flex-col items-end text-slate-100">
                                <span className="text-xs text-slate-300">
                                  Remaining
                                </span>
                                <span className="text-sm font-semibold">
                                  {credit.remaining_days} day
                                  {credit.remaining_days === 1 ? '' : 's'}
                                </span>
                                <span className="mt-0.5 text-[11px] text-slate-400">
                                  Used {used} / {credit.days} days
                                </span>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    <div className="mt-6 border-t border-white/10 pt-4">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Your leave requests
                        </h3>
                      </div>

                      {leaveRequestsLoading ? (
                        <p className="text-xs text-slate-300">Loading leave requests...</p>
                      ) : leaveRequests.length === 0 ? (
                        <p className="text-xs text-slate-300">
                          You haven&apos;t filed any leave requests yet. New requests you submit
                          will appear here with status{' '}
                          <span className="font-semibold text-slate-100">Waiting for Approval</span>.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-left text-xs text-slate-200">
                            <thead>
                              <tr className="border-b border-white/10 text-[11px] uppercase tracking-[0.16em] text-slate-400">
                                <th className="py-2 pr-4 font-medium">Date range</th>
                                <th className="py-2 pr-4 font-medium">Reason</th>
                                <th className="py-2 pr-2 text-right font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                              {leaveRequests.map((req) => (
                                <tr key={req.id}>
                                  <td className="py-2 pr-4 align-top">
                                    {new Date(req.date_from).toLocaleDateString()} –{' '}
                                    {new Date(req.date_to).toLocaleDateString()}
                                  </td>
                                  <td className="py-2 pr-4 align-top max-w-xs">
                                    <span className="line-clamp-2 text-slate-300">{req.reason}</span>
                                  </td>
                                  <td className="py-2 pr-2 text-right align-top">
                                    <span className="inline-flex items-center rounded-full bg-slate-900/80 px-3 py-1 text-[11px] font-semibold text-amber-300">
                                      {req.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </section>
          ) : (
            <section className="grid gap-6">
              <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 to-slate-950/70 p-6 shadow-xl shadow-black/40">
                <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-sm font-semibold tracking-tight text-white">Task tracker</h2>
                    <p className="text-[11px] text-slate-400">
                      Manage what you&apos;re working on with simple boards.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2">
                      <label htmlFor="task-tracker-project" className="text-[11px] font-medium text-slate-400">
                        Project
                      </label>
                      <select
                        id="task-tracker-project"
                        value={selectedProjectId}
                        onChange={(e) => setSelectedProjectId(e.target.value)}
                        disabled={projectsLoading}
                        className="rounded-lg border border-white/20 bg-slate-900/80 px-3 py-1.5 text-xs text-slate-100 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:opacity-60"
                      >
                        <option value="">All projects</option>
                        {employeeProjects.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.project_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddColumn}
                      disabled={taskLoading}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-slate-900/80 px-3 py-1.5 text-[11px] font-medium text-slate-100 hover:border-white/40 hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <span className="text-lg leading-none">＋</span>
                      Add column
                    </button>
                  </div>
                </div>

                {taskError && (
                  <div className="mb-4 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs text-red-100">
                    {taskError}
                  </div>
                )}

                {taskLoading ? (
                  <p className="py-8 text-sm text-slate-300">Loading task tracker...</p>
                ) : (
                <div className="flex flex-col gap-4 pb-1 md:flex-row md:overflow-x-auto">
                  {filteredTaskColumns.map((column, columnIndex) => (
                    <div
                      key={column.id}
                      className="flex w-full flex-col rounded-2xl border border-white/10 bg-slate-950/80 p-3 md:w-64 md:shrink-0"
                    >
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-sky-400" />
                          <p className="text-xs font-semibold text-slate-50">{column.title}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-medium text-slate-200">
                            {column.tasks.length}
                          </span>
                          <div className="flex items-center gap-0.5">
                            <button
                              type="button"
                              onClick={() => handleMoveColumn(column.id, 'left')}
                              disabled={columnIndex === 0}
                              className={`h-6 w-6 rounded-full border text-[10px] ${
                                columnIndex === 0
                                  ? 'border-slate-700 text-slate-600 cursor-not-allowed'
                                  : 'border-white/10 text-slate-200 hover:border-white/30 hover:bg-slate-800'
                              }`}
                              aria-label="Move column left"
                            >
                              ←
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveColumn(column.id, 'right')}
                              disabled={columnIndex === filteredTaskColumns.length - 1}
                              className={`h-6 w-6 rounded-full border text-[10px] ${
                                columnIndex === taskColumns.length - 1
                                  ? 'border-slate-700 text-slate-600 cursor-not-allowed'
                                  : 'border-white/10 text-slate-200 hover:border-white/30 hover:bg-slate-800'
                              }`}
                              aria-label="Move column right"
                            >
                              →
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteColumn(column.id)}
                              className="h-6 w-6 rounded-full border border-red-500/40 text-[10px] text-red-200 hover:border-red-400 hover:bg-red-500/10"
                              aria-label="Delete column"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="mb-2 h-px bg-white/5" />
                      <div className="flex-1 space-y-2">
                        {column.tasks.length === 0 ? (
                          <p className="text-[11px] text-slate-500">No tasks yet.</p>
                        ) : (
                          column.tasks.map((task) => (
                            <div
                              key={task.id}
                              className="rounded-xl border border-white/5 bg-white/5 px-3 py-2 text-xs text-slate-100"
                            >
                              {task.projectName && (
                                <span
                                  className="mb-1.5 inline-block truncate rounded bg-sky-500/20 px-1.5 py-0.5 text-[10px] font-medium text-sky-300"
                                  title={task.projectName}
                                >
                                  {task.projectName}
                                </span>
                              )}
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-xs text-slate-100">{task.title}</p>
                                <div className="flex flex-col items-end gap-1">
                                  {taskColumns.length > 1 && (
                                    <>
                                      {columnIndex > 0 && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleMoveTask(column.id, task.id, 'left')
                                          }
                                          className="inline-flex items-center rounded-full bg-slate-900/80 px-2 py-0.5 text-[10px] font-medium text-slate-200 hover:bg-slate-800"
                                        >
                                          ← Prev
                                        </button>
                                      )}
                                      {columnIndex < taskColumns.length - 1 && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleMoveTask(column.id, task.id, 'right')
                                          }
                                          className="inline-flex items-center rounded-full bg-slate-900/80 px-2 py-0.5 text-[10px] font-medium text-slate-200 hover:bg-slate-800"
                                        >
                                          Next →
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleAddTask(column.id)}
                        disabled={taskLoading}
                        className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-sky-300 hover:text-sky-200 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <span className="text-base leading-none">＋</span>
                        Add task
                      </button>
                    </div>
                  ))}
                </div>
                )}
              </div>

              <div className="mt-6 rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 to-slate-950/70 p-6 shadow-xl shadow-black/40">
                <h3 className="mb-3 text-sm font-semibold tracking-tight text-white">
                  Completed tasks (project)
                </h3>
                <p className="mb-4 text-[11px] text-slate-400">
                  Tasks marked as completed in the project task tracker. For your monitoring.
                </p>
                {completedTasksLoading ? (
                  <p className="py-4 text-xs text-slate-500">Loading…</p>
                ) : completedTasks.length === 0 ? (
                  <p className="py-4 text-xs text-slate-500">No completed tasks yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {completedTasks.map((task) => (
                      <li
                        key={task.id}
                        className="flex flex-wrap items-center gap-2 rounded-xl border border-white/5 bg-slate-950/80 px-3 py-2 text-xs"
                      >
                        <span className="font-medium text-slate-100">{task.title}</span>
                        {task.project_name && (
                          <span className="rounded bg-sky-500/20 px-1.5 py-0.5 text-[10px] text-sky-300">
                            {task.project_name}
                          </span>
                        )}
                        {task.sprint_name && (
                          <span className="text-slate-500">{task.sprint_name}</span>
                        )}
                        {task.completed_at && (
                          <span className="ml-auto text-[10px] text-slate-500">
                            {new Date(task.completed_at).toLocaleDateString(undefined, {
                              dateStyle: 'short',
                            })}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}

          {isApplyLeaveOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
              <div className="w-full max-w-md rounded-3xl border border-white/10 bg-slate-950 p-6 shadow-2xl shadow-black/60">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-semibold tracking-tight text-white">
                      Apply for leave
                    </h2>
                    <p className="mt-1 text-xs text-slate-300">
                      Choose the leave type, date range, and share a short reason.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleCloseApplyLeave}
                    className="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 text-xs text-slate-300 hover:border-white/40 hover:text-white"
                  >
                    ×
                  </button>
                </div>

                {leaveSubmitError && (
                  <div className="mb-3 rounded-2xl border border-red-500/50 bg-red-500/10 px-3 py-2 text-[11px] text-red-100">
                    {leaveSubmitError}
                  </div>
                )}

                <form className="space-y-3 text-xs" onSubmit={handleSubmitLeave}>
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-slate-200">
                      What leave are you using?
                    </label>
                    <select
                      value={selectedLeaveCreditId}
                      onChange={(e) => setSelectedLeaveCreditId(e.target.value)}
                      className="w-full rounded-2xl border border-white/15 bg-slate-900/70 px-3 py-2 text-xs text-slate-50 outline-none ring-0 transition focus:border-brand-400 focus:ring-1 focus:ring-brand-500/40"
                    >
                      <option value="">Select leave type</option>
                      {leaveCredits.map((credit) => (
                        <option key={credit.id} value={credit.id}>
                          {credit.credit_name} · {credit.remaining_days} day
                          {credit.remaining_days === 1 ? '' : 's'} left
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-slate-200">
                        Start date
                      </label>
                      <input
                        type="date"
                        value={leaveDateFrom}
                        onChange={(e) => setLeaveDateFrom(e.target.value)}
                        className="w-full rounded-2xl border border-white/15 bg-slate-900/70 px-3 py-2 text-xs text-slate-50 outline-none ring-0 transition focus:border-brand-400 focus:ring-1 focus:ring-brand-500/40"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-slate-200">
                        End date
                      </label>
                      <input
                        type="date"
                        value={leaveDateTo}
                        onChange={(e) => setLeaveDateTo(e.target.value)}
                        className="w-full rounded-2xl border border-white/15 bg-slate-900/70 px-3 py-2 text-xs text-slate-50 outline-none ring-0 transition focus:border-brand-400 focus:ring-1 focus:ring-brand-500/40"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-slate-200">
                      Reason for leave
                    </label>
                    <textarea
                      value={leaveReason}
                      onChange={(e) => setLeaveReason(e.target.value)}
                      rows={3}
                      placeholder="Briefly explain why you&apos;re taking leave."
                      className="w-full resize-none rounded-2xl border border-white/15 bg-slate-900/70 px-3 py-2 text-xs text-slate-50 outline-none ring-0 transition placeholder:text-slate-500 focus:border-brand-400 focus:ring-1 focus:ring-brand-500/40"
                    />
                  </div>

                  <div className="mt-4 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleCloseApplyLeave}
                      disabled={leaveSubmitting}
                      className="inline-flex items-center rounded-full border border-white/20 bg-slate-900/60 px-4 py-1.5 text-[11px] font-medium text-slate-200 hover:border-white/40 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={leaveSubmitting}
                      className="inline-flex items-center rounded-full border border-brand-400/70 bg-brand-500 px-4 py-1.5 text-[11px] font-semibold text-slate-950 shadow-sm shadow-brand-500/40 transition hover:bg-brand-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {leaveSubmitting ? 'Submitting…' : 'Submit request'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <p className="mt-10 text-center text-[11px] text-slate-500">
            HR & Payroll · Employee attendance & leave overview
          </p>
        </motion.div>
      </main>
    </div>
  );
}
